/* istanbul ignore file */
import d3 from 'd3';
import nv from 'nvd3';
import get from 'lodash/get';

export const ADVANCED_SYMBOLS = 2;

export function formatCharData(data, isAdvanceSymbols) {
  const chartData = [...data];
  if (!isAdvanceSymbols) {
    return chartData;
  }

  [
    'Accepted',
    'Waitlisted/Accepted',
    'Waitlisted/Unknown',
    'Waitlisted/Denied',
    'Denied',
  ].forEach((key) => {
    const collapsedIdx = data.findIndex((el) => el.key === key);
    const dataItem = collapsedIdx > -1 ? data[collapsedIdx] : null;
    const values = get(dataItem, `values`, []);

    if (values.length) {
      const advancedItemIdx = data.findIndex((el) => el.key === `${key} (RD)`);

      if (advancedItemIdx > -1) {
        const advancedItemValues = chartData[advancedItemIdx].values;
        chartData[advancedItemIdx].values = [...advancedItemValues, ...values];
      }
    }

    if (collapsedIdx > -1) {
      chartData[collapsedIdx] = null;
    }
  });

  return chartData.filter((i) => i); // remove empty items
}

export function initZoom(chartRef) {
  setTimeout(() => {
    const chart = get(chartRef, 'current.chart', null);

    if (!chart) {
      return false;
    }

    addZoom({
      xAxis: chart.xAxis,
      yAxis: chart.yAxis,
      yDomain: chart.yDomain,
      xDomain: chart.xDomain,
      redraw: function () {
        chart.update();
      },
    });

    nv.utils.windowResize(chart.update);
  }, 200);
}

function addZoom(options) {
  const scaleExtent = 10;
  const yAxis = options.yAxis;
  const xAxis = options.xAxis;
  const xDomain = options.xDomain || xAxis.scale().domain;
  const yDomain = options.yDomain || yAxis.scale().domain;
  const redraw = options.redraw;
  const discrete = options.discrete;

  // scales
  const xScale = xAxis.scale();
  const yScale = yAxis.scale();

  // min/max boundaries
  const x_boundary = xScale.domain().slice();
  const y_boundary = yScale.domain().slice();

  // create d3 zoom handler
  const d3zoom = d3.behavior.zoom();

  // ensure nice axis
  xScale.nice();
  yScale.nice();

  // fix domain
  function fixDomain(domain, boundary) {
    if (discrete) {
      domain[0] = parseInt(domain[0]);
      domain[1] = parseInt(domain[1]);
    }
    domain[0] = Math.min(
      Math.max(domain[0], boundary[0]),
      boundary[1] - boundary[1] / scaleExtent,
    );
    domain[1] = Math.max(
      boundary[0] + boundary[1] / scaleExtent,
      Math.min(domain[1], boundary[1]),
    );
    return domain;
  }

  // zoom event handler
  function zoomed() {
    yDomain(fixDomain(yScale.domain(), y_boundary));
    xDomain(fixDomain(xScale.domain(), x_boundary));
    redraw();
  }

  // zoom event handler
  function unzoomed() {
    xDomain(x_boundary);
    yDomain(y_boundary);
    redraw();
    d3zoom.scale(1);
    d3zoom.translate([0, 0]);
  }

  // initialize wrapper
  d3zoom.x(xScale).y(yScale).scaleExtent([1, scaleExtent]).on('zoom', zoomed);

  // add handler
  d3.select('#nvd3Scattergram').call(d3zoom).on('dblclick.zoom', unzoomed);
}

export function initNv() {
  nv.utils.symbolMap.set('h-dash', function (size) {
    let offset = size / 2;
    return `M ${-offset}, 0 l ${size}, 0 Z`;
  });

  nv.utils.symbolMap.set('v-dash', function (size) {
    let offset = size / 2;
    return 'M 0, ' + -offset + ' l0 , ' + size + ' Z';
  });

  nv.utils.symbolMap.set('thin-x', function (size) {
    size = Math.sqrt(size);
    return (
      'M' +
      -size / 2 +
      ',' +
      -size / 2 +
      'l' +
      size +
      ',' +
      size +
      'm0,' +
      -size +
      'l' +
      -size +
      ',' +
      size
    );
  });

  nv.utils.symbolMap.set('thick-x', function (size) {
    let r = (1 / 2) * Math.sqrt(size);
    let t = r / 5;
    return (
      'M' +
      -t +
      ',' +
      0 +
      'L' +
      (-r - t) +
      ',' +
      (-r + t) +
      'L' +
      (-r + t) +
      ',' +
      (-r - t) +
      'L' +
      0 +
      ',' +
      -t +
      'L' +
      (r - t) +
      ',' +
      (-r - t) +
      'L' +
      (r + t) +
      ',' +
      (-r + t) +
      'L' +
      t +
      ',' +
      0 +
      'L' +
      (r + t) +
      ',' +
      (r - t) +
      'L' +
      (r - t) +
      ',' +
      (r + t) +
      'L' +
      0 +
      ',' +
      t +
      'L' +
      (-r + t) +
      ',' +
      (r + t) +
      'L' +
      (-r - t) +
      ',' +
      (r - t) +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('rhombus', function (size) {
    let r = (2 / 3) * Math.sqrt(size);
    return (
      'M0,' +
      r +
      'A' +
      r +
      ',' +
      r +
      ' 0 1,1 0,' +
      -r +
      'A' +
      r +
      ',' +
      r +
      ' 0 1,1 0,' +
      r +
      'Z'
    );
  });

  nv.utils.symbolMap.set('rhombus-triangle', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('x-triangle', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((22 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('checkmark', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((44 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((44 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((40 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((40 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((44 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((20 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('checksquare', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((30 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((20 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((20 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((18 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((34 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((44 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((46 - offsetY) * scale).toString() +
      ' L' +
      ((20 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('square-triangle', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('square-rhombus', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('square-x', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((34 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((30 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((30 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('circle-rhombus', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('circle-x', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((34 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((30 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((30 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((52 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('circle-square', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      'M' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((50 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((14 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('circle-check', function (size) {
    let scale = size / 400;
    let offsetX = 32;
    let offsetY = 32;
    return (
      ' M' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((40 - offsetX) * scale).toString() +
      ' ' +
      ((34 - offsetY) * scale).toString() +
      ' L' +
      ((44 - offsetX) * scale).toString() +
      ' ' +
      ((26 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((18 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((58 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((30 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((30 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((62 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((14 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((20 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((34 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((44 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((46 - offsetY) * scale).toString() +
      ' L' +
      ((22 - offsetX) * scale).toString() +
      ' ' +
      ((50 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' Z'
    );
  });

  nv.utils.symbolMap.set('student', function (size) {
    let scale = size / 700;
    let offsetX = 32;
    let offsetY = 32;
    return (
      'M' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((22 - offsetX) * scale).toString() +
      ' ' +
      ((18 - offsetY) * scale).toString() +
      ' L' +
      ((22 - offsetX) * scale).toString() +
      ' ' +
      ((18 - offsetY) * scale).toString() +
      ' L' +
      ((22 - offsetX) * scale).toString() +
      ' ' +
      ((20 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((30 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((64 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((56 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((0 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((4 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((0 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((8 - offsetX) * scale).toString() +
      ' ' +
      ((54 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((56 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((60 - offsetY) * scale).toString() +
      ' L' +
      ((26 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((64 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((24 - offsetX) * scale).toString() +
      ' ' +
      ((40 - offsetY) * scale).toString() +
      ' L' +
      ((20 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((44 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((2 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((4 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((6 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((10 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((12 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((16 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((28 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((2 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((6 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' L' +
      ((54 - offsetX) * scale).toString() +
      ' ' +
      ((10 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((16 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((22 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((62 - offsetX) * scale).toString() +
      ' ' +
      ((36 - offsetY) * scale).toString() +
      ' L' +
      ((60 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((58 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((52 - offsetX) * scale).toString() +
      ' ' +
      ((48 - offsetY) * scale).toString() +
      ' L' +
      ((48 - offsetX) * scale).toString() +
      ' ' +
      ((44 - offsetY) * scale).toString() +
      ' L' +
      ((44 - offsetX) * scale).toString() +
      ' ' +
      ((42 - offsetY) * scale).toString() +
      ' L' +
      ((40 - offsetX) * scale).toString() +
      ' ' +
      ((40 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((32 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((36 - offsetX) * scale).toString() +
      ' ' +
      ((38 - offsetY) * scale).toString() +
      ' L' +
      ((34 - offsetX) * scale).toString() +
      ' ' +
      ((32 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((28 - offsetY) * scale).toString() +
      ' L' +
      ((40 - offsetX) * scale).toString() +
      ' ' +
      ((24 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((20 - offsetY) * scale).toString() +
      ' L' +
      ((42 - offsetX) * scale).toString() +
      ' ' +
      ((18 - offsetY) * scale).toString() +
      ' L' +
      ((40 - offsetX) * scale).toString() +
      ' ' +
      ((12 - offsetY) * scale).toString() +
      ' L' +
      ((38 - offsetX) * scale).toString() +
      ' ' +
      ((8 - offsetY) * scale).toString() +
      ' Z'
    );
  });
}

// get featureFlag value by featureFlag name
export const getFeatureFlagValue = (
  featureFlags,
  featureFlagName,
  defaultValue = false,
) => {
  return get(featureFlags, featureFlagName, defaultValue);
};
